/**
 * 10.9EO — ARENA DA RAID + CAÇADORES DA SALA.
 *
 * A arena fica FORA do mapa (GameConfig.raid.arena), como a Fenda: uma plataforma circular com
 * runas e pilares. Os outros jogadores da SALA (ate 40) aparecem como caçadores leves (sem IA,
 * sem fisica), com nome e barra de vida, suavizando as 4 atualizacoes/s que chegam do servidor.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { makeLabel, disposeLabel } from "../world/StructureLabel";
import type { RemoteState } from "../net/RaidNet";

const CLASS_COLOR: Record<string, number> = { warrior: 0xff8a5a, archer: 0xffd24a, mage: 0xb48bff, necromancer: 0x7fe0b0 };

export class RaidArena {
  readonly group = new THREE.Group();
  private t = 0;
  private readonly runes: THREE.Mesh;

  constructor(scene: THREE.Scene) {
    const a = GameConfig.raid.arena;
    this.group.position.set(a.x, 0, a.z);
    const floor = new THREE.Mesh(
      new THREE.CylinderGeometry(a.r + 2, a.r + 3, 1.2, 64),
      new THREE.MeshStandardMaterial({ color: 0x15122a, roughness: 0.95, flatShading: true }),
    );
    floor.position.y = -0.6;
    floor.receiveShadow = true;
    this.group.add(floor);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xffc14a, transparent: true, opacity: 0.55 });
    const edge = new THREE.Mesh(new THREE.TorusGeometry(a.r + 0.6, 0.18, 6, 96), ringMat);
    edge.rotation.x = Math.PI / 2;
    edge.position.y = 0.05;
    this.group.add(edge);
    // runas concentricas que giram devagar
    const runeMat = new THREE.MeshBasicMaterial({ color: 0x9b5cff, transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false });
    this.runes = new THREE.Mesh(new THREE.RingGeometry(9, 9.5, 64, 1), runeMat);
    this.runes.rotation.x = -Math.PI / 2;
    this.runes.position.y = 0.04;
    this.group.add(this.runes);
    for (const r of [15, 21]) {
      const m = new THREE.Mesh(new THREE.RingGeometry(r, r + 0.25, 64, 1), runeMat);
      m.rotation.x = -Math.PI / 2;
      m.position.y = 0.04;
      this.group.add(m);
    }
    // pilares na borda
    const pilMat = new THREE.MeshStandardMaterial({ color: 0x2a2446, emissive: 0x3a1a80, emissiveIntensity: 0.4, roughness: 0.6, flatShading: true });
    const tipMat = new THREE.MeshBasicMaterial({ color: 0xffc14a });
    for (let i = 0; i < 10; i++) {
      const ang = (i / 10) * Math.PI * 2;
      const x = Math.cos(ang) * (a.r + 2.2);
      const z = Math.sin(ang) * (a.r + 2.2);
      const pil = new THREE.Mesh(new THREE.BoxGeometry(1.1, 6 + (i % 3), 1.1), pilMat);
      pil.position.set(x, (6 + (i % 3)) / 2, z);
      pil.castShadow = true;
      this.group.add(pil);
      const tip = new THREE.Mesh(new THREE.OctahedronGeometry(0.45, 0), tipMat);
      tip.position.set(x, 6.6 + (i % 3), z);
      this.group.add(tip);
    }
    this.group.visible = false;
    scene.add(this.group);
  }

  setVisible(v: boolean): void { this.group.visible = v; }
  update(dt: number): void { this.t += dt; this.runes.rotation.z = this.t * 0.15; }
}

interface Remote {
  group: THREE.Group;
  label: THREE.Object3D;
  bar: THREE.Mesh;
  tx: number; tz: number; f: number;
  seen: number;
}

/** Caçadores dos OUTROS jogadores da sala. */
export class RemoteHunters {
  private readonly list = new Map<string, Remote>();
  private readonly geo = {
    body: new THREE.CylinderGeometry(0.3, 0.55, 1.15, 8),
    head: new THREE.SphereGeometry(0.26, 8, 6),
    hood: new THREE.ConeGeometry(0.34, 0.55, 7),
    bar: new THREE.PlaneGeometry(1.1, 0.12),
  };
  private readonly mats = new Map<number, { cloth: THREE.MeshStandardMaterial; glow: THREE.MeshStandardMaterial }>();
  private readonly barMat = new THREE.MeshBasicMaterial({ color: 0x55e07a, depthTest: false });

  constructor(private readonly scene: THREE.Scene) {}

  private matsFor(color: number): { cloth: THREE.MeshStandardMaterial; glow: THREE.MeshStandardMaterial } {
    let m = this.mats.get(color);
    if (!m) {
      m = {
        cloth: new THREE.MeshStandardMaterial({ color: 0x1b1630, emissive: color, emissiveIntensity: 0.22, roughness: 0.85, flatShading: true }),
        glow: new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.8, roughness: 0.4, flatShading: true }),
      };
      this.mats.set(color, m);
    }
    return m;
  }

  private create(s: RemoteState): Remote {
    const color = CLASS_COLOR[s.cls ?? ""] ?? 0x9fb4ff;
    const { cloth, glow } = this.matsFor(color);
    const g = new THREE.Group();
    const body = new THREE.Mesh(this.geo.body, cloth); body.position.y = 0.6; g.add(body);
    const head = new THREE.Mesh(this.geo.head, glow); head.position.y = 1.42; g.add(head);
    const hood = new THREE.Mesh(this.geo.hood, cloth); hood.position.y = 1.7; g.add(hood);
    const label = makeLabel(s.name ?? "Caçador", "#" + color.toString(16).padStart(6, "0"), 2.6);
    g.add(label);
    const bar = new THREE.Mesh(this.geo.bar, this.barMat);
    bar.position.y = 2.15;
    bar.renderOrder = 999;
    g.add(bar);
    g.position.set(s.x, 0, s.z);
    this.scene.add(g);
    return { group: g, label, bar, tx: s.x, tz: s.z, f: s.f, seen: 0 };
  }

  apply(ps: RemoteState[]): void {
    for (const s of ps) {
      let r = this.list.get(s.id);
      if (!r) { r = this.create(s); this.list.set(s.id, r); }
      r.tx = s.x; r.tz = s.z; r.f = s.f; r.seen = 0;
      const frac = s.mhp > 0 ? Math.max(0, Math.min(1, s.hp / s.mhp)) : 1;
      r.bar.scale.x = Math.max(0.02, frac);
      r.group.visible = frac > 0; // caido: some ate voltar
    }
  }

  update(dt: number, camera: THREE.Camera): void {
    const k = Math.min(1, dt * 10);
    for (const [id, r] of this.list) {
      r.seen += dt;
      if (r.seen > 6) { this.remove(id); continue; } // sumiu da sala (caiu da rede)
      r.group.position.x += (r.tx - r.group.position.x) * k;
      r.group.position.z += (r.tz - r.group.position.z) * k;
      r.group.rotation.y = r.f;
      r.bar.quaternion.copy(camera.quaternion);
    }
  }

  get count(): number { return this.list.size; }

  private remove(id: string): void {
    const r = this.list.get(id);
    if (!r) return;
    this.scene.remove(r.group);
    disposeLabel(r.label);
    this.list.delete(id);
  }

  clear(): void { for (const id of [...this.list.keys()]) this.remove(id); }
}
